import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, GripVertical, Factory, Store, Boxes } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { productionService } from '@/services/production';
import { inventoryService } from '@/services/inventory';
import { vendorsService } from '@/services/vendors';
import { pickerLabel } from '@/lib/inventory-label';
import type { ProductionStepMode, SaveRecipePayload } from '@/types/production';

interface Props {
  recipeId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface DraftInput {
  key: number;
  inputItemId: string;
  qty: string;
}
interface DraftStep {
  key: number;
  name: string;
  mode: ProductionStepMode;
  defaultVendorId: string;
  outputMode: 'NEW' | 'EXISTING'; // intermediate steps only
  newOutputName: string;
  outputItemId: string;
  outputQtyPerRun: string;
  expectedWastagePct: string;
  instructions: string;
  inputs: DraftInput[];
}

let K = 1;
const newInput = (): DraftInput => ({ key: K++, inputItemId: '', qty: '' });
const newStep = (): DraftStep => ({
  key: K++,
  name: '',
  mode: 'IN_HOUSE',
  defaultVendorId: '',
  outputMode: 'NEW',
  newOutputName: '',
  outputItemId: '',
  outputQtyPerRun: '1',
  expectedWastagePct: '0',
  instructions: '',
  inputs: [newInput()],
});

/**
 * Recipe editor — the "how a finished good is made" authoring surface. Define
 * the ordered production steps, each with its inputs (BOM), output (a new
 * intermediate or, on the last step, the finished good), and mode (in-house
 * or outsourced to a job-work contractor). Saves the whole route in one PUT.
 */
export function RecipeEditorSheet({ recipeId, open, onOpenChange }: Props) {
  const qc = useQueryClient();

  const recipeQuery = useQuery({
    queryKey: ['production', 'recipe', recipeId],
    queryFn: () => productionService.getRecipe(recipeId!),
    enabled: open && !!recipeId,
  });
  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { forRecipe: true }],
    queryFn: () => inventoryService.list({ status: 'ACTIVE', pageSize: 300 }),
    enabled: open,
    staleTime: 30_000,
  });
  const vendorsQuery = useQuery({
    queryKey: ['vendors', { forSelect: true }],
    queryFn: () => vendorsService.list({ status: 'ACTIVE', pageSize: 200 }),
    enabled: open,
    staleTime: 60_000,
  });

  const recipe = recipeQuery.data;
  const items = itemsQuery.data?.items ?? [];
  const vendors = vendorsQuery.data?.items ?? [];
  // Inputs can be raw materials or earlier steps' semi-finished outputs.
  const inputItems = useMemo(
    () => items.filter((i) => i.kind === 'RAW_MATERIAL' || i.kind === 'SEMI_FINISHED'),
    [items],
  );

  const [steps, setSteps] = useState<DraftStep[]>([]);
  const [notes, setNotes] = useState('');

  // Seed the draft from the loaded recipe.
  useEffect(() => {
    if (!recipe) return;
    setNotes(recipe.notes ?? '');
    if (recipe.steps.length === 0) {
      setSteps([newStep()]);
      return;
    }
    setSteps(
      recipe.steps
        .slice()
        .sort((a, b) => a.sequence - b.sequence)
        .map((s, i, arr) => {
          const isFinal = i === arr.length - 1;
          return {
            key: K++,
            name: s.name,
            mode: s.mode,
            defaultVendorId: s.defaultVendorId ?? '',
            outputMode: isFinal ? 'EXISTING' : s.outputItem.kind === 'SEMI_FINISHED' ? 'NEW' : 'EXISTING',
            newOutputName: isFinal ? '' : s.outputItem.kind === 'SEMI_FINISHED' ? s.outputItem.name : '',
            outputItemId: s.outputItemId,
            outputQtyPerRun: String(s.outputQtyPerRun ?? 1),
            expectedWastagePct: String(s.expectedWastagePct ?? 0),
            instructions: s.instructions ?? '',
            inputs: s.inputs.length
              ? s.inputs.map((inp) => ({
                  key: K++,
                  inputItemId: inp.inputItemId,
                  qty: String(inp.qtyPerOutputUnit),
                }))
              : [newInput()],
          };
        }),
    );
  }, [recipe]);

  const patchStep = (key: number, patch: Partial<DraftStep>) =>
    setSteps((ss) => ss.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const patchInput = (sKey: number, iKey: number, patch: Partial<DraftInput>) =>
    setSteps((ss) =>
      ss.map((s) =>
        s.key === sKey
          ? { ...s, inputs: s.inputs.map((i) => (i.key === iKey ? { ...i, ...patch } : i)) }
          : s,
      ),
    );

  const save = useMutation({
    mutationFn: () => {
      const payload: SaveRecipePayload = {
        notes: notes.trim() || undefined,
        steps: steps.map((s, idx) => {
          const isFinal = idx === steps.length - 1;
          return {
            sequence: idx + 1,
            name: s.name.trim(),
            mode: s.mode,
            defaultVendorId: s.mode === 'OUTSOURCED' && s.defaultVendorId ? s.defaultVendorId : undefined,
            // Final step outputs the recipe's finished good (backend defaults it);
            // intermediate steps create-new or reuse.
            ...(isFinal
              ? {}
              : s.outputMode === 'NEW'
                ? { newOutputName: s.newOutputName.trim() }
                : { outputItemId: s.outputItemId }),
            outputQtyPerRun: Number(s.outputQtyPerRun) || 1,
            expectedWastagePct: Number(s.expectedWastagePct) || 0,
            instructions: s.instructions.trim() || undefined,
            inputs: s.inputs
              .filter((i) => i.inputItemId && Number(i.qty) > 0)
              .map((i) => ({ inputItemId: i.inputItemId, qtyPerOutputUnit: Number(i.qty) })),
          };
        }),
      };
      // Light client-side guard.
      for (const [i, s] of payload.steps.entries()) {
        if (!s.name) throw new Error(`Step ${i + 1} needs a name.`);
        if (i < payload.steps.length - 1 && !s.newOutputName && !s.outputItemId)
          throw new Error(`Step ${i + 1} needs an output (name a new intermediate or pick one).`);
        if (s.mode === 'OUTSOURCED' && !s.defaultVendorId)
          throw new Error(`Step ${i + 1} is outsourced — pick a contractor.`);
        if (s.inputs.length === 0) throw new Error(`Step ${i + 1} needs at least one input material.`);
      }
      return productionService.saveRecipe(recipeId!, payload);
    },
    onSuccess: () => {
      toast.success('Recipe saved');
      qc.invalidateQueries({ queryKey: ['production', 'recipe', recipeId] });
      qc.invalidateQueries({ queryKey: ['production', 'recipes'] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Could not save recipe'),
  });

  const activate = useMutation({
    mutationFn: () => productionService.activateRecipe(recipeId!),
    onSuccess: () => {
      toast.success('Recipe published');
      qc.invalidateQueries({ queryKey: ['production', 'recipe', recipeId] });
      qc.invalidateQueries({ queryKey: ['production', 'recipes'] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Could not publish'),
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15">
            <Factory className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Recipe · how it's made
            </div>
            <div className="flex items-center gap-2">
              <span className="truncate text-[18px] font-semibold leading-tight">
                {recipe ? recipe.targetItem.name : 'Recipe'}
              </span>
              {recipe && (
                <Badge variant={recipe.status === 'ACTIVE' ? 'success' : 'muted'} className="text-[10px]">
                  {recipe.status}
                </Badge>
              )}
              {recipe?.source === 'LEARNED' && (
                <Badge variant="outline" className="text-[10px]">
                  suggested{recipe.confidence != null ? ` · ${recipe.confidence}%` : ''}
                </Badge>
              )}
            </div>
            <div className="text-[12px] text-muted-foreground">
              {recipe ? `${recipe.targetItem.sku} · v${recipe.version}` : ''} — raw materials flow through
              each step to the finished good. Each step becomes a job work order.
            </div>
          </div>
        </div>
      }
      title=""
      submitLabel="Save recipe"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      isSubmitting={save.isPending}
      size="6xl"
      fixedHeight="xl"
      extraFooter={
        recipe && recipe.status !== 'ACTIVE' ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={activate.isPending || save.isPending}
            onClick={() => activate.mutate()}
          >
            Publish recipe
          </Button>
        ) : undefined
      }
    >
      {recipeQuery.isLoading ? (
        <div className="h-40 animate-pulse rounded-md bg-muted" />
      ) : (
        <div className="space-y-4">
          {/* Route diagram */}
          <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border/60 bg-muted/20 p-2.5 text-[11.5px]">
            <span className="inline-flex items-center gap-1 text-muted-foreground"><Boxes className="h-3.5 w-3.5" /> Raw</span>
            {steps.map((s, i) => (
              <span key={s.key} className="inline-flex items-center gap-1.5">
                <span className="text-muted-foreground">→</span>
                <Badge variant="outline" className="gap-1 text-[10px]">
                  {s.mode === 'OUTSOURCED' ? <Store className="h-3 w-3" /> : <Factory className="h-3 w-3" />}
                  {s.name || `Step ${i + 1}`}
                </Badge>
              </span>
            ))}
            <span className="text-muted-foreground">→</span>
            <Badge variant="success" className="text-[10px]">{recipe?.targetItem.name ?? 'Finished good'}</Badge>
          </div>

          {steps.map((s, idx) => {
            const isFinal = idx === steps.length - 1;
            return (
              <div key={s.key} className="rounded-xl border border-border/70 bg-card p-4">
                <div className="mb-3 flex items-center gap-2">
                  <GripVertical className="h-4 w-4 text-muted-foreground/50" />
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-primary/15 text-[11px] font-bold text-primary">
                    {idx + 1}
                  </span>
                  <Input
                    value={s.name}
                    placeholder={isFinal ? 'Final step (e.g. Finishing & QC)' : 'Step name (e.g. Cutting)'}
                    onChange={(e) => patchStep(s.key, { name: e.target.value })}
                    className="h-8 max-w-xs"
                  />
                  {isFinal && <Badge variant="success" className="text-[10px]">produces finished good</Badge>}
                  <button
                    type="button"
                    className="ml-auto text-muted-foreground hover:text-destructive disabled:opacity-30"
                    disabled={steps.length === 1}
                    onClick={() => setSteps((ss) => ss.filter((x) => x.key !== s.key))}
                    aria-label="Remove step"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-3">
                    <FormField label="Done by" htmlFor={`step-${s.key}-mode`}>
                      <Select value={s.mode} onValueChange={(v) => patchStep(s.key, { mode: v as ProductionStepMode })}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="IN_HOUSE">In-house</SelectItem>
                          <SelectItem value="OUTSOURCED">Outsourced (contractor)</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>
                  </div>
                  {s.mode === 'OUTSOURCED' && (
                    <div className="col-span-4">
                      <FormField label="Contractor (default)" htmlFor={`step-${s.key}-vendor`}>
                        <Select value={s.defaultVendorId} onValueChange={(v) => patchStep(s.key, { defaultVendorId: v })}>
                          <SelectTrigger className="h-9"><SelectValue placeholder="Pick a vendor" /></SelectTrigger>
                          <SelectContent>
                            {vendors.map((v) => (
                              <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormField>
                    </div>
                  )}
                  <div className="col-span-2">
                    <FormField label="Output qty / run" htmlFor={`step-${s.key}-oqty`}>
                      <Input type="number" min={0} step="0.001" value={s.outputQtyPerRun}
                        onChange={(e) => patchStep(s.key, { outputQtyPerRun: e.target.value })} className="h-9 text-right tabular-nums" />
                    </FormField>
                  </div>
                  <div className="col-span-3">
                    <FormField label="Wastage %" hint="expected" htmlFor={`step-${s.key}-waste`}>
                      <Input type="number" min={0} max={100} step="0.5" value={s.expectedWastagePct}
                        onChange={(e) => patchStep(s.key, { expectedWastagePct: e.target.value })} className="h-9 text-right tabular-nums" />
                    </FormField>
                  </div>
                </div>

                {/* Output (intermediate steps only — final = finished good) */}
                {!isFinal && (
                  <div className="mt-3">
                    <FormField label="This step produces" htmlFor={`step-${s.key}-out`}>
                      <div className="flex gap-2">
                        <Select value={s.outputMode} onValueChange={(v) => patchStep(s.key, { outputMode: v as 'NEW' | 'EXISTING' })}>
                          <SelectTrigger className="h-9 w-44"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NEW">New intermediate</SelectItem>
                            <SelectItem value="EXISTING">Existing item</SelectItem>
                          </SelectContent>
                        </Select>
                        {s.outputMode === 'NEW' ? (
                          <Input value={s.newOutputName} placeholder="e.g. Cut panels"
                            onChange={(e) => patchStep(s.key, { newOutputName: e.target.value })} className="h-9 flex-1" />
                        ) : (
                          <Select value={s.outputItemId} onValueChange={(v) => patchStep(s.key, { outputItemId: v })}>
                            <SelectTrigger className="h-9 flex-1"><SelectValue placeholder="Pick semi-finished item" /></SelectTrigger>
                            <SelectContent>
                              {items.filter((i) => i.kind === 'SEMI_FINISHED').map((i) => (
                                <SelectItem key={i.id} value={i.id}>{pickerLabel(i)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </FormField>
                  </div>
                )}

                {/* Inputs (BOM) */}
                <div className="mt-3 rounded-lg border border-border/50 bg-muted/20 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Consumes (per output unit)
                    </span>
                    <Button type="button" size="sm" variant="ghost" className="h-7"
                      onClick={() => patchStep(s.key, { inputs: [...s.inputs, newInput()] })}>
                      <Plus className="h-3.5 w-3.5" /> Add material
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {s.inputs.map((inp) => (
                      <div key={inp.key} className="flex items-center gap-2">
                        <Select value={inp.inputItemId} onValueChange={(v) => patchInput(s.key, inp.key, { inputItemId: v })}>
                          <SelectTrigger className="h-8 flex-1"><SelectValue placeholder="Raw material or earlier output" /></SelectTrigger>
                          <SelectContent>
                            {inputItems.map((i) => (
                              <SelectItem key={i.id} value={i.id}>
                                {pickerLabel(i)}
                                <span className="ml-2 text-[10px] text-muted-foreground">
                                  {i.kind === 'SEMI_FINISHED' ? 'semi' : 'raw'}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input type="number" min={0} step="0.001" value={inp.qty} placeholder="qty"
                          onChange={(e) => patchInput(s.key, inp.key, { qty: e.target.value })}
                          className="h-8 w-24 text-right tabular-nums" />
                        <button type="button" className="text-muted-foreground hover:text-destructive disabled:opacity-30"
                          disabled={s.inputs.length === 1}
                          onClick={() => patchStep(s.key, { inputs: s.inputs.filter((x) => x.key !== inp.key) })}
                          aria-label="Remove material">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {!isFinal && (
                  <Textarea value={s.instructions} placeholder="Step instructions (optional)"
                    onChange={(e) => patchStep(s.key, { instructions: e.target.value })}
                    className="mt-3 min-h-[40px] text-[12.5px]" />
                )}
              </div>
            );
          })}

          <Button type="button" variant="outline" size="sm" onClick={() => setSteps((ss) => [...ss, newStep()])}>
            <Plus className="h-4 w-4" /> Add step
          </Button>
        </div>
      )}
    </FormSheet>
  );
}
